import { useState } from 'react';
import { useApp } from '../store';
import { can } from '../permissions';
import { describeAddresses, joinName } from '../people';
import { PROJECT_ROLES, ROLE_LABEL, ROLE_BLURB, type ProjectRole } from '../permissions';
import {
  EMPTY_DRAFT,
  STEPS,
  finishSummary,
  keyFor,
  stepAnswered,
  stepProblem,
  teamFrom,
  type Draft,
} from '../onboarding';
import { DELIVERY_MODE_LABEL, DELIVERY_MODE_BLURB } from '../projects';
import Icon from './Icon';
import Logo from './Logo';

/**
 * BEING WALKED THROUGH IT, RATHER THAN SHOWN A BUTTON.
 *
 * Three steps — you, your team, your first board — in that order and for the
 * reasons set out in src/onboarding.ts. This file is the screen; the decisions
 * are next door, so they can be argued about without a browser.
 *
 * NOTHING IS WRITTEN UNTIL THE LAST STEP. Somebody who abandons this half way
 * leaves nothing behind: no half-saved name, and no people invited into a
 * workspace with no board in it. The cost is that the last press does three
 * things at once, so it says which three before it is pressed.
 *
 * THE BOARD IS MADE FIRST OF THE THREE, and the people are added to it rather
 * than to nothing. Adding somebody to a workspace with no boards puts them
 * exactly where this screen started — being told there is nothing here.
 */
export default function Welcome() {
  const role = useApp((s) => s.shownRole);
  const user = useApp((s) => s.user);
  const access = useApp((s) => s.access);
  const projects = useApp((s) => s.projects);
  const archivedProjects = useApp((s) => s.archivedProjects);
  const restoreSamples = useApp((s) => s.restoreSamples);
  const setPersonProfile = useApp((s) => s.setPersonProfile);
  const createProject = useApp((s) => s.createProject);
  const setDeliveryMode = useApp((s) => s.setDeliveryMode);
  const addToProject = useApp((s) => s.addToProject);

  const [at, setAt] = useState(0);
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  // What kind of board this first one is. A project is the pre-selected default,
  // the same Step-0 choice the New-board flow offers; a continuous service is
  // born with no plan/budget scaffolding.
  const [kind, setKind] = useState<'project' | 'continuous'>('project');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');

  const step = STEPS[at];
  const last = at === STEPS.length - 1;
  // The one quiet way back when the samples were a tenant's only boards and an
  // admin removed them — so an empty picker is never a dead end (mock 03 edge,
  // R15). Admin-only (the authority that archives boards) and only on the first
  // step; bringing them back otherwise lives solely in Settings → Workspace.
  const canBringBack =
    at === 0 && can(role, 'manageWorkspace') && archivedProjects.some((p) => p.sample);
  const set = (patch: Partial<Draft>) => setDraft((d) => ({ ...d, ...patch }));
  const adding = teamFrom(draft, access.emails);

  /*
   * SOMEBODY WHO CANNOT MAKE A BOARD IS NOT SHOWN A WIZARD THEY CANNOT FINISH.
   * They are not stuck, they are waiting for somebody — and three steps ending
   * in a refusal is a worse way to learn that than one sentence.
   */
  if (!can(role, 'createProject')) {
    return (
      <div className="wz">
        <div className="wz-card">
          <Logo tile={44} mark={27} />
          <h2>Nothing here yet</h2>
          <p className="wz-lead">
            No boards have been made, and your account cannot make them. Ask an administrator to
            start one, or to give you the rights to.
          </p>
        </div>
      </div>
    );
  }

  const next = async () => {
    const wrong = stepProblem(step.id, draft, projects);
    if (wrong) return setProblem(wrong);
    setProblem('');
    if (!last) return setAt(at + 1);

    setBusy(true);
    try {
      // The board first, so the people below are added TO something. A continuous
      // board is born with no phases (no project scaffolding); its delivery mode
      // is set right after, while this is still the open board.
      const id = await createProject(
        draft.boardName,
        keyFor(draft),
        kind === 'continuous' ? { phases: [] } : undefined
      );
      await setDeliveryMode(kind);
      for (const email of adding.ready) await addToProject(id, email, draft.teamRole);
      const name = joinName(draft.firstName, draft.lastName);
      if (name)
        await setPersonProfile(user?.email ?? '', {
          name,
          firstName: draft.firstName.trim(),
          lastName: draft.lastName.trim(),
          ...(draft.title.trim() ? { title: draft.title.trim() } : {}),
        });
      // createProject already switched to it, so there is nothing to navigate
      // to — the board simply appears underneath as this screen stops being
      // the one with no boards.
    } catch (e) {
      setBusy(false);
      const why = e instanceof Error ? e.message : String(e);
      setProblem(
        /permission/i.test(why)
          ? 'Your account was not allowed to do that. Ask an administrator.'
          : `It could not be finished — ${why}`
      );
    }
  };

  const body = () => {
    switch (step.id) {
      case 'you':
        return (
          <>
            <div className="acc-pair">
              <label className="stack">
                <span className="stack-lbl">First name <span className="req">*</span></span>
                <input
                  className="input"
                  autoFocus
                  value={draft.firstName}
                  onChange={(e) => set({ firstName: e.target.value })}
                />
              </label>
              <label className="stack">
                Last name
                <input
                  className="input"
                  value={draft.lastName}
                  onChange={(e) => set({ lastName: e.target.value })}
                />
              </label>
            </div>
            <label className="stack">
              What you do here
              <input
                className="input"
                value={draft.title}
                onChange={(e) => set({ title: e.target.value })}
                placeholder="e.g. Developer, or Product owner"
              />
            </label>
          </>
        );
      case 'team':
        return (
          <>
            {/* TWO ANSWERS, NEITHER OF THEM A SKIP. Plenty of people genuinely
                are on their own, and that is a thing to say rather than a step
                to escape — so it is a choice, and the screen can tell it apart
                from an empty box nobody has looked at. */}
            <div className="feedback-kinds" role="radiogroup" aria-label="Who else is here">
              <button
                type="button"
                role="radio"
                aria-checked={draft.solo === false}
                className={`feedback-kind ${draft.solo === false ? 'is-on' : ''}`}
                onClick={() => set({ solo: false })}
              >
                <span className="feedback-kind-title">I will add my team now</span>
                <span className="feedback-kind-blurb">
                  They get an email with a link that signs them in.
                </span>
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={draft.solo === true}
                className={`feedback-kind ${draft.solo === true ? 'is-on' : ''}`}
                onClick={() => set({ solo: true, team: '' })}
              >
                <span className="feedback-kind-title">It is just me for now</span>
                <span className="feedback-kind-blurb">
                  You can add people whenever you like, from People and access.
                </span>
              </button>
            </div>

            {draft.solo === false && (
              <>
                <label className="stack">
                  Their email addresses
                  <textarea
                    className="input wz-team"
                    rows={3}
                    autoFocus
                    value={draft.team}
                    onChange={(e) => set({ team: e.target.value })}
                    placeholder="one each, or paste a list"
                  />
                </label>
                {draft.team.trim() && <p className="hint">{describeAddresses(adding)}</p>}
              </>
            )}
            {draft.solo === false && (
            <label className="stack">
              What they can do
              <select
                className="input"
                value={draft.teamRole}
                onChange={(e) => set({ teamRole: e.target.value as ProjectRole })}
              >
                {PROJECT_ROLES.map((r) => (
                  <option key={r} value={r}>
                    {ROLE_LABEL[r]}
                  </option>
                ))}
              </select>
              <span className="hint">{ROLE_BLURB[draft.teamRole]}</span>
            </label>
            )}
            {/* SAID HERE BECAUSE THIS IS WHERE THE QUESTION OCCURS. Somebody
                choosing what other people may do wonders what they themselves
                are, and "Admin" is not in the list — deliberately. Admin is a
                WORKSPACE rank; the top of the per-board ladder is Project
                manager, and making a board hands it to you. */}
            <p className="hint">
              You are the <strong>project manager</strong> of the board you are about to make —
              its settings, its issues and who may do what on it. <em>Admin</em> is separate: it is
              the whole workspace, not one board.
            </p>
          </>
        );
      default:
        return (
          <>
            {/* STEP 0, FOLDED INTO THE FIRST BOARD. The same project-vs-continuous
                choice the New-board flow opens with — a project is the default,
                and a continuous service starts with no plan, budget or sign-off. */}
            <div className="stack">
              <span className="stack-lbl">What kind of board is this?</span>
              <div className="mode-cards" role="radiogroup" aria-label="What kind of board is this?">
                {(['project', 'continuous'] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    role="radio"
                    aria-checked={kind === m}
                    className={`mode-card ${kind === m ? 'is-on' : ''}`}
                    onClick={() => setKind(m)}
                  >
                    <span className="mode-card-title">
                      {DELIVERY_MODE_LABEL[m]}
                      {m === 'project' && <span className="person-you">default</span>}
                    </span>
                    <span className="mode-card-blurb">{DELIVERY_MODE_BLURB[m]}</span>
                  </button>
                ))}
              </div>
            </div>
            <label className="stack">
              What is it called?
              <input
                className="input"
                autoFocus
                value={draft.boardName}
                onChange={(e) => set({ boardName: e.target.value })}
                placeholder="Website redesign"
              />
            </label>
            <label className="stack">
              A short key <span className="hint">goes in front of every issue number</span>
              <input
                className="input key-input"
                value={keyFor(draft)}
                onChange={(e) => set({ keyIsTheirs: true, boardKey: e.target.value })}
              />
              {/* Shown rather than described: "a key goes in front of every
                  issue number" is abstract until you see WR#1. */}
              <span className="hint">
                {keyFor(draft)
                  ? `Issues will be numbered ${keyFor(draft)}#1, ${keyFor(draft)}#2, and so on.`
                  : ' '}
              </span>
            </label>
          </>
        );
    }
  };

  return (
    <div className="wz">
      <div className="wz-card">
        <Logo tile={44} mark={27} />

        <div className="wz-progress" aria-hidden="true">
          {STEPS.map((s, n) => (
            <span key={s.id} className={`wz-dot ${n === at ? 'is-now' : n < at ? 'is-done' : ''}`} />
          ))}
        </div>
        <span className="wz-count">
          Step {at + 1} of {STEPS.length}
        </span>

        <h2>{step.title}</h2>
        <p className="wz-lead">{step.blurb}</p>

        <div className="wz-body">{body()}</div>

        {problem && (
          <p className="feedback-problem" role="alert">
            {problem}
          </p>
        )}

        {/* WHAT THE LAST PRESS WILL DO, before it is pressed. Nothing is
            written until then, so it does three things at once. */}
        {last && !problem && (
          <p className="hint wz-summary">{finishSummary(draft, adding.ready.length)}</p>
        )}

        <div className="wz-foot">
          {at > 0 && (
            <button className="btn" onClick={() => setAt(at - 1)} disabled={busy}>
              Back
            </button>
          )}
          <span className="wz-spacer" />
          {/* NO "SKIP". These three steps ARE the account, the team and the
              workspace — there is nothing to skip to. The button is simply
              held until the step has an answer, and on the team step "it is
              just me" is one of the answers. */}
          <button
            className="btn btn-primary"
            onClick={() => void next()}
            disabled={busy || !stepAnswered(step.id, draft)}
            title={stepAnswered(step.id, draft) ? '' : stepProblem(step.id, draft, projects)}
          >
            {busy ? 'Setting up…' : last ? 'Make the board' : 'Next'}
            {!busy && !last && <Icon name="chevron" size={18} />}
          </button>
        </div>

        {/* One quiet line, only when there are removed examples to restore. */}
        {canBringBack && (
          <button className="wz-restore" onClick={() => void restoreSamples()} disabled={busy}>
            <Icon name="eye" size={15} />
            Or bring back the samples
          </button>
        )}
      </div>
    </div>
  );
}
